// src/hooks/useAnalysis.js
import { useState, useCallback } from 'react';
import { claudeAPI } from '@services/claudeAPI';
import { ERROR_MESSAGES, SCORING_WEIGHTS } from '@config/constants';
import { scoreKeywordMatch } from '@services/keywordAnalysis';

function localAtsScore(resumeText, jobText) {
  const { matched, missing, relevanceScore } = scoreKeywordMatch(jobText, resumeText);

  const contactOk = /@/.test(resumeText) || /linkedin\.com/i.test(resumeText);
  const hasSections =
    /(experience|education|skills|projects|summary)/i.test(resumeText) ? 1 : 0;

  const formatting = Math.min(100, 60 + (hasSections ? 40 : 0));
  const keywords = relevanceScore;
  const structure = hasSections ? 90 : 60;
  const contact = contactOk ? 85 : 50;

  const overall = Math.round(
    (SCORING_WEIGHTS.ATS_FORMAT * formatting +
      SCORING_WEIGHTS.KEYWORD_MATCH * keywords +
      SCORING_WEIGHTS.CONTENT_STRUCTURE * structure +
      SCORING_WEIGHTS.CONTACT_INFO * contact) /
      (SCORING_WEIGHTS.ATS_FORMAT +
        SCORING_WEIGHTS.KEYWORD_MATCH +
        SCORING_WEIGHTS.CONTENT_STRUCTURE +
        SCORING_WEIGHTS.CONTACT_INFO)
  );

  return {
    keywordAnalysis: { matched, missing, relevanceScore: keywords },
    atsScore: {
      overall,
      breakdown: { formatting, keywords, structure, contact },
    },
    strengths: [
      ...(hasSections ? ['Clear section headings detected'] : []),
      ...(contactOk ? ['Contact information present'] : []),
      ...(matched.length > 0 ? ['Some job keywords already present'] : []),
    ],
    weaknesses: [
      ...(hasSections ? [] : ['Add standard sections like Experience / Education / Skills']),
      ...(contactOk ? [] : ['Add a professional email and/or LinkedIn URL']),
      ...(missing.length > 0 ? ['Missing several role-specific keywords'] : []),
    ],
    recommendations: [
      'Tailor bullet points to mirror exact job terminology where relevant.',
      'Quantify achievements (impact, scale, speed, savings).',
      'Keep formatting simple (no tables/columns) for ATS compatibility.',
    ],
    aiEnhancedSuggestions: [],
  };
}

export function useAnalysis() {
  const [results, setResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  const clearResults = useCallback(() => {
    setResults(null);
    setError(null);
  }, []);

  const analyzeResume = useCallback(async (resumeText, jobDescription) => {
    setIsAnalyzing(true);
    setError(null);
    setResults(null);

    try {
      const ai = await claudeAPI.analyzeResume(resumeText, jobDescription);
      if (!ai || typeof ai !== 'object') throw new Error('Unexpected AI response');
      setResults(ai);
      return ai;
    } catch (err) {
      const msg = String(err?.message || '');
      const looksLikeRegexError =
        /Invalid regular expression/i.test(msg) || /Nothing to repeat/i.test(msg);

      const local = localAtsScore(resumeText, jobDescription);
      setResults(local);

      if (looksLikeRegexError) {
        setError(
          'A keyword contained special characters (e.g., "C++"). I used a safe matcher and completed the analysis locally.'
        );
      } else {
        setError(ERROR_MESSAGES.API_ERROR);
      }
      return local;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  return { results, isAnalyzing, error, analyzeResume, clearResults };
}

export default useAnalysis;
